import { Zap, Gauge, Activity, TrendingUp } from "lucide-react";

const Bar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[11px] mb-1"><span className="text-white/40 uppercase tracking-wide">{label}</span><span className="text-white/70 font-mono-accent">{value}</span></div>
    <div className="w-full h-1.5 bg-white/10"><div className="h-full bg-white" style={{ width: `${value}%` }} /></div>
  </div>
);

export default function AcademyOverview({ snapshot, performance, speedPoints }) {
  const s = snapshot || {};
  const perf = performance || {};
  const bd = perf.breakdown || {};
  const sp = speedPoints || {};
  const stats = [
    { label: "Best 10-Yard", value: s.best_ten_yard },
    { label: "Best Flying 10", value: s.best_flying_ten },
    { label: "Vertical", value: s.best_vertical },
    { label: "Broad Jump", value: s.best_broad },
    { label: "Speed Score", value: s.speed_score },
    { label: "Training Phase", value: s.training_phase },
  ];

  return (
    <div className="space-y-6" data-testid="academy-overview">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Performance Score */}
        <div className="bg-[#121212] border border-white/10 p-6" data-testid="academy-performance">
          <p className="flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4"><Gauge size={13} /> Performance Score</p>
          <div className="flex items-end gap-2 mb-5">
            <span className="font-display font-black text-6xl leading-none tracking-tighter" data-testid="academy-perf-value">{perf.score ?? "—"}</span>
            <span className="text-white/40 mb-2 text-lg">/ 100</span>
          </div>
          <div className="space-y-3">
            <Bar label="Attendance" value={bd.attendance ?? 0} />
            <Bar label="Testing Improvement" value={bd.testing_improvement ?? 0} />
            <Bar label="Coach Evaluation" value={bd.coach_evaluation ?? 0} />
            <Bar label="Consistency" value={bd.consistency ?? 0} />
          </div>
        </div>

        {/* Speed Points */}
        <div className="bg-[#121212] border border-white/10 p-6" data-testid="academy-speed-points">
          <p className="flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4"><Zap size={13} /> Speed Points</p>
          <div className="flex gap-8 mb-5">
            <div><p className="font-display font-black text-5xl leading-none tracking-tighter" data-testid="academy-sp-lifetime">{sp.lifetime ?? 0}</p><p className="text-white/40 text-[11px] uppercase tracking-wide mt-1">Lifetime SP</p></div>
            <div><p className="font-display font-black text-5xl leading-none tracking-tighter text-white/70">{sp.current ?? 0}</p><p className="text-white/40 text-[11px] uppercase tracking-wide mt-1">Last 30 Days</p></div>
          </div>
          <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/30 mb-2">Recent Activity</p>
          {(sp.recent || []).length === 0 ? (
            <p className="text-white/30 text-sm">No SP yet — attend training and set PRs to earn Speed Points.</p>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="academy-sp-recent">
              {(sp.recent || []).slice(0, 8).map((r, i) => (
                <div key={`${r.created_at}-${i}`} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5">
                  <span className="text-white/60 truncate mr-2 flex items-center gap-1.5"><TrendingUp size={12} className="text-emerald-400 shrink-0" />{r.reason}</span>
                  <span className="font-mono-accent text-white/80 shrink-0">+{r.points}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <p className="flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-3"><Activity size={13} /> Snapshot</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10">
          {stats.map((st) => (
            <div key={st.label} className="bg-[#0A0A0A] p-4" data-testid={`academy-stat-${st.label.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <p className="font-mono-accent text-lg md:text-xl font-bold break-words leading-tight">{st.value ?? "—"}</p>
              <p className="text-white/40 text-[10px] uppercase tracking-wide mt-1">{st.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
